"use client";

import { StatCard, StatGrid } from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { SHAPIRO_WILK_TEST_CALCULATOR_STRINGS } from "@/components/tools/shapiro-wilk-test-calculator";
import {
  ApaReport,
  Decision,
  Working,
} from "@/components/tools/stats-tests/kit";
import { QqPlot } from "@/components/tools/stats-tests/qq-plot";
import { t } from "@/lib/i18n/interpolate";
import {
  mean,
  quantile,
  standardDeviation,
  sumOfSquares,
} from "@/lib/stats/descriptive";
import {
  apaP,
  fixed,
  formatP,
  formatStat,
  type NumberFormatter,
  richTemplate,
} from "@/lib/tool-kit/stats-tests/format";
import {
  qqPoints,
  type ShapiroWilkResult,
  shapiroWilkCoefficients,
  skewnessKurtosis,
} from "@/lib/tool-kit/stats-tests/shapiro-wilk";

/** Results for the Shapiro–Wilk calculator. */

type Strings = typeof SHAPIRO_WILK_TEST_CALCULATOR_STRINGS;

/** b = Σ aᵢ(x₍ₙ₊₁₋ᵢ₎ − x₍ᵢ₎), the numerator of W before squaring. */
function numerator(values: readonly number[]): number {
  const x = [...values].sort((p, q) => p - q);
  const a = shapiroWilkCoefficients(x.length);
  let b = 0;
  for (const [i, coefficient] of a.entries()) {
    b += coefficient * ((x.at(-1 - i) as number) - x[i]);
  }
  return b;
}

function methodNote(n: number, strings: Strings): string {
  if (n === 3) {
    return strings.methodExact;
  }
  return n <= 11 ? strings.methodSmall : strings.methodLarge;
}

export function ShapiroWilkResults({
  r,
  values,
  alpha,
  large,
  small,
  strings,
  fmt,
}: {
  r: ShapiroWilkResult;
  values: readonly number[];
  alpha: number;
  large: boolean;
  small: boolean;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const n = (v: number) => formatStat(v, fmt);
  const shape = skewnessKurtosis(values);
  const m = mean(values);
  const sd = standardDeviation(values);
  const reject = r.p < alpha;
  const apa = richTemplate(strings.apaTemplate, {
    n: fmt(r.n),
    w: fixed(r.w, 2, fmt, false),
    p: apaP(r.p, fmt),
  });
  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard emphasis label={strings.wStatistic} value={n(r.w)} />
        <StatCard emphasis label={strings.pValue} value={formatP(r.p, fmt)} />
        <StatCard label={strings.n} value={fmt(r.n)} />
        <StatCard label={strings.skewness} value={n(shape.skewness)} />
        <StatCard
          label={strings.kurtosis}
          value={
            Number.isNaN(shape.excessKurtosis) ? "—" : n(shape.excessKurtosis)
          }
        />
        <StatCard label={strings.mean} value={n(m)} />
        <StatCard label={strings.sd} value={n(sd)} />
      </StatGrid>
      <Decision reject={reject}>
        {t(reject ? strings.decisionReject : strings.decisionKeep, {
          alpha: fmt(alpha),
          p: formatP(r.p, fmt),
        })}
      </Decision>
      {large ? (
        <p className={HINT}>{t(strings.largeSample, { n: fmt(r.n) })}</p>
      ) : null}
      {small ? (
        <p className={HINT}>{t(strings.smallSample, { n: fmt(r.n) })}</p>
      ) : null}
      <p className={HINT}>{strings.shapeHint}</p>
      <h3 className={WIDGET_H3}>{strings.plotHeading}</h3>
      <p className={HINT}>{strings.plotHint}</p>
      <QqPlot
        downloadLabel={strings.downloadPlot}
        fmt={fmt}
        label={t(strings.plotLabel, { n: fmt(r.n) })}
        points={qqPoints(values)}
        q1={quantile(values, 0.25)}
        q3={quantile(values, 0.75)}
        xTitle={strings.xTitle}
        yTitle={strings.yTitle}
      />
      <Working heading={strings.howCalculated}>
        <p>
          {t(strings.workingW, {
            b: n(numerator(values)),
            ss: n(sumOfSquares(values)),
            w: n(r.w),
          })}
        </p>
        <p>
          {t(strings.workingP, {
            method: methodNote(r.n, strings),
            p: formatP(r.p, fmt),
          })}
        </p>
      </Working>
      <ApaReport
        copiedLabel={strings.copied}
        copyLabel={strings.copyApa}
        heading={strings.apaHeading}
        note={strings.apaNote}
        parts={apa}
      />
    </section>
  );
}
